import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { CheckSquare, Wallet, Package, Loader2, ArrowLeft, UsersRound } from "lucide-react";

/**
 * A module-scoped login page. Renders either the Team Room (combined HR +
 * Petty Cash) or Quotes branding depending on the `module` prop, and calls
 * the auth API with a `module` field so the backend rejects wrong-role accounts.
 */

const MODULE_STYLES = {
  "team-room": {
    title: "Team Room",
    tagline: "HR · Attendance · Cash",
    icon: UsersRound,
    tone: "hsl(var(--brand-teal))",
    hero: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1400&q=80",
    quote: "\u201CGood shifts and good books — one room, one team.\u201D",
    quoteCite: "\u2014 Store floor",
    cta: "Sign in to Team Room",
    home: "/presently",
    roles: null,
  },
  presently: {
    title: "Team Room",
    tagline: "HR · Attendance · Cash",
    icon: UsersRound,
    tone: "hsl(var(--brand-teal))",
    hero: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1400&q=80",
    quote: "\u201CGood shifts and good books — one room, one team.\u201D",
    quoteCite: "\u2014 Store floor",
    cta: "Sign in to Team Room",
    home: "/presently",
    roles: null,
  },
  "petty-cash": {
    title: "Team Room",
    tagline: "HR · Attendance · Cash",
    icon: UsersRound,
    tone: "hsl(var(--brand-teal))",
    hero: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=1400&q=80",
    quote: "\u201CGood shifts and good books — one room, one team.\u201D",
    quoteCite: "\u2014 Store floor",
    cta: "Sign in to Team Room",
    home: "/petty-cash",
    roles: null,
  },
  quotes: {
    title: "Quotes",
    tagline: "Sales · Dispatch",
    icon: Package,
    tone: "hsl(var(--status-leave))",
    hero: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1400&q=80",
    quote: "\u201CEvery quote is a promise waiting to be kept.\u201D",
    quoteCite: "\u2014 Sales floor",
    cta: "Sign in to Quotes",
    home: "/quotes",
    roles: [
      { id: "sales_team", label: "Sales", hint: "Create quotations" },
      { id: "management", label: "Management", hint: "Approve · dashboard" },
      { id: "dispatch_team", label: "Dispatch", hint: "Pack · ship" },
    ],
  },
};

function GoogleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" {...props}>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4-5.5 4-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.4 14.7 2.4 12 2.4 6.8 2.4 2.6 6.6 2.6 12S6.8 21.6 12 21.6c6.9 0 9.5-4.8 9.5-7.4 0-.5 0-.9-.1-1.3H12z" />
    </svg>
  );
}

export default function LoginPage({ module = "team-room" }) {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const cfg = MODULE_STYLES[module] || MODULE_STYLES["team-room"];
  const Icon = cfg.icon;
  const home = cfg.home || "/";

  const [tab, setTab] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState(cfg.roles ? cfg.roles[0].id : null);

  // If a matching session already exists (correct role for this module), skip login.
  useEffect(() => {
    if (!user) return;
    const teamRoles = ["admin", "staff", "hr", "store_manager"];
    const allowedByModule = {
      "team-room": teamRoles,
      presently: teamRoles,
      "petty-cash": teamRoles,
      quotes: ["admin", "sales_team", "management", "dispatch_team"],
    };
    const allowed = allowedByModule[module] || [];
    if (allowed.includes(user.role)) {
      navigate(home, { replace: true });
    }
  }, [user, module, navigate, home]);

  // Show one-time toast when redirected here because of role mismatch.
  useEffect(() => {
    if (location.state?.wrongRole) {
      toast.error(`Your account isn't provisioned for ${cfg.title}. Please use the correct login.`);
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location, cfg.title]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // The team-room module uses /workspace/auth/callback (there is no /team-room route).
    const callbackPath = module === "team-room" ? "workspace" : module;
    const redirectUrl = window.location.origin + `/${callbackPath}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "signin") {
        await login(email.trim(), password, module);
        toast.success("Welcome back");
        navigate(home);
      } else {
        const result = await register(name.trim(), email.trim(), password, module, role);
        if (result?.pending) {
          toast.success(result.message || "Request submitted. Awaiting admin approval.", {
            duration: 7000,
          });
          // Clear form fields, switch back to sign-in tab
          setPassword("");
          setName("");
          setTab("signin");
        } else {
          toast.success("Account created");
          navigate(home);
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grain-bg bloom-bg" data-testid={`login-page-${module}`}>
      {/* Left: form */}
      <div className="flex items-center justify-center p-8 lg:p-16 relative">
        <Link
          to="/"
          data-testid="back-to-chooser"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted-foreground hover:text-brand font-mono-mini transition-colors"
        >
          <ArrowLeft className="h-3 w-3" /> All modules
        </Link>

        <div className="w-full max-w-md anim-fade-up">
          {/* Text-only brand header (logo intentionally omitted per user's spec) */}
          <Link to="/" className="inline-flex items-center gap-2 mb-9 group" data-testid="login-brand-link">
            <span className="h-1.5 w-6 rounded-full bg-brand" />
            <span className="font-display text-base font-bold tracking-tight text-foreground group-hover:text-brand transition-colors">
              SpaceAttire Teams
            </span>
          </Link>

          <div className="flex items-center gap-3 mb-10">
            <div
              className="h-11 w-11 rounded-xl grid place-items-center shadow-soft"
              style={{ background: cfg.tone, color: "white" }}
            >
              <Icon className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-display text-2xl font-bold tracking-tight leading-none">{cfg.title}</div>
              <div className="eyebrow mt-1.5">
                {cfg.tagline}
              </div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight">
            {cfg.cta.split(" to ")[0]}<br />
            <span className="text-brand italic font-normal">to {cfg.title}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-4 max-w-sm leading-relaxed">
            Each module has its own login. If your account isn't for {cfg.title}, we'll let you know.
          </p>

          <div className="mt-10">
            <Button
              type="button"
              onClick={handleGoogle}
              variant="outline"
              className="w-full h-11 rounded-xl border-border bg-card hover:bg-secondary btn-lift shadow-soft"
              data-testid="google-signin-btn"
            >
              <GoogleIcon className="mr-2" />
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-mono-mini">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-2 w-full rounded-xl bg-secondary p-1">
                <TabsTrigger value="signin" data-testid="tab-signin" className="rounded-lg">Sign in</TabsTrigger>
                <TabsTrigger value="signup" data-testid="tab-signup" className="rounded-lg">Create account</TabsTrigger>
              </TabsList>

              <form onSubmit={submit} className="mt-5 space-y-4">
                {tab === "signup" && (
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
                    <Input
                      id="name"
                      data-testid="input-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="mt-1.5 h-11"
                    />
                  </div>
                )}

                {tab === "signup" && cfg.roles && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your role</Label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {cfg.roles.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          data-testid={`role-${r.id}`}
                          onClick={() => setRole(r.id)}
                          className={[
                            "text-left px-3 py-2 rounded-md border transition-colors",
                            role === r.id
                              ? "border-foreground bg-foreground text-background"
                              : "border-border hover:border-foreground/40",
                          ].join(" ")}
                        >
                          <div className="text-xs font-medium">{r.label}</div>
                          <div className="text-[10px] uppercase tracking-widest opacity-70 mt-0.5">{r.hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="input-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1.5 h-11"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="submit-auth-btn"
                  disabled={loading}
                  className="w-full h-11 rounded-xl btn-lift shadow-soft font-medium"
                  style={{ background: cfg.tone, color: "white" }}
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {tab === "signin" ? cfg.cta : `Create ${cfg.title} account`}
                </Button>

                {tab === "signup" && (
                  <p className="text-[11px] text-muted-foreground pt-1">
                    This creates a {cfg.title} account. Once an admin approves you, you can
                    sign in and use every section.
                  </p>
                )}
              </form>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Right: hero */}
      <div className="hidden lg:block relative overflow-hidden border-l border-border">
        <img src={cfg.hero} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(160deg, hsl(var(--brand-teal-deep) / 0.72), hsl(var(--brand-teal) / 0.55) 60%, hsl(var(--brand-peach) / 0.28))" }}
        />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <div className="flex items-center gap-2 text-white/90">
            <div className="h-2 w-2 rounded-full bg-white" />
            <span className="font-mono-mini text-[10px] uppercase tracking-[0.3em]">
              {cfg.tagline}
            </span>
          </div>
          <div className="max-w-md text-white">
            <p className="font-display text-3xl xl:text-4xl font-bold leading-[1.15] tracking-tight">
              {cfg.quote}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="h-px w-8 bg-white/60" />
              <div className="text-xs opacity-85 font-mono-mini uppercase tracking-widest">{cfg.quoteCite}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
